import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, CheckCircle2, XCircle, FileSpreadsheet, Download, Upload } from "lucide-react";
import { SKUMasterCombobox } from "@/components/SKUMasterCombobox";

const EMPTY = { formula_id: "", name: "", sku: "", batch_output_qty: 0, unit: "CASE", version: "v1", active: true, remarks: "" };

function BulkUploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setPreview(null); } }, [open]);
  async function downloadTemplate() {
    const token = localStorage.getItem("oms_token");
    const res = await fetch(`${API}/formulas/template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const b = await res.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "formulas_template.xlsx"; a.click();
  }
  async function doPreview() {
    if (!file) return toast.error("Upload an xlsx file");
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/formulas/bulk-preview`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed"); setPreview(d);
    } catch (e) { toast.error(e.message); }
  }
  async function doApply() {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/formulas/bulk-apply`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed");
      toast.success(`Created ${d.created}, updated ${d.updated}`); onOpenChange(false); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading">Bulk Upload · Formulas</DialogTitle><DialogDescription className="text-sm text-slate-500">Upsert formulas by (Formula ID + SKU) pair. The same Formula ID can be mapped to multiple SKUs. Rows with Active=Yes auto-deactivate other active formulas for the same SKU.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div className="col-span-2"><Label>XLSX File</Label><Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="formula-bulk-file" /></div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="formula-template"><Download className="h-4 w-4 mr-1" /> Template</Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doPreview}><FileSpreadsheet className="h-4 w-4 mr-1" /> Preview</Button>
          <Button onClick={doApply} disabled={busy || !file} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="formula-bulk-apply"><Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Apply"}</Button>
        </div>
        {preview && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-3 bg-slate-50 border-b text-sm">{preview.count} rows · <b>{preview.items.filter((i) => i.action === "create").length}</b> new · <b>{preview.items.filter((i) => i.action === "update").length}</b> updates</div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0"><tr><th className="p-2 text-left">Action</th><th className="p-2 text-left">Formula</th><th className="p-2 text-left">SKU</th><th className="p-2 text-left">Name</th><th className="p-2 text-right">Batch</th><th className="p-2 text-left">Active</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.items.map((it, i) => (<tr key={i}><td className="p-2"><span className={`text-[10px] px-2 py-0.5 rounded border ${it.action === "create" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{it.action}</span></td><td className="p-2 mono">{it.formula_id}</td><td className="p-2 mono">{it.sku}</td><td className="p-2">{it.name}</td><td className="p-2 text-right">{it.batch_output_qty}</td><td className="p-2 text-xs">{it.active ? "Yes" : "No"}</td></tr>))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Formulas() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin", "production", "operations"].includes(user?.role);
  const canDelete = ["super_admin", "admin", "operations"].includes(user?.role);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  async function load() { const { data } = await api.get("/formulas"); setList(data); setSelected(new Set()); }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => list.filter((f) => !search || (f.formula_id + " " + f.sku + " " + f.name).toLowerCase().includes(search.toLowerCase())), [list, search]);

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }
  function toggleAll() { setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((r) => r.id))); }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} formula(s)?`)) return;
    try { const { data } = await api.post("/formulas/bulk-delete", { ids: Array.from(selected) }); toast.success(`${data.deleted} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  function startNew() { setForm(EMPTY); setEditId(null); setOpen(true); }
  function startEdit(f) { setForm({ ...f }); setEditId(f.id); setOpen(true); }

  async function save() {
    try {
      const payload = { ...form, batch_output_qty: Number(form.batch_output_qty || 0), active: !!form.active };
      if (editId) await api.patch(`/formulas/${editId}`, payload);
      else await api.post("/formulas", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function remove(f) {
    if (!window.confirm(`Delete formula ${f.formula_id}?`)) return;
    try { await api.delete(`/formulas/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Formula Master</h1>
          <p className="text-sm text-slate-500 mt-1">One active formula per SKU. The same Formula ID can be mapped to multiple SKUs. Used by the Production Sheet to compute batch counts.</p>
        </div>
        {canEdit && <div className="flex gap-2">
          {canDelete && selected.size > 0 && <Button onClick={bulkDelete} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="formula-bulk-delete"><Trash2 className="h-4 w-4 mr-1.5" /> Delete {selected.size}</Button>}
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="formula-bulk-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Bulk Upload</Button>
          <Button onClick={startNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-formula-btn"><Plus className="h-4 w-4 mr-1.5" /> New Formula</Button>
        </div>}
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by formula id / SKU / name…" className="pl-9" data-testid="formula-search" />
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                {canDelete && <th className="p-3.5 w-8"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="formula-select-all" /></th>}
                <th className="p-3.5 text-left">Formula ID</th>
                <th className="p-3.5 text-left">Name</th>
                <th className="p-3.5 text-left">SKU</th>
                <th className="p-3.5 text-right">Batch Output</th>
                <th className="p-3.5 text-left">Unit</th>
                <th className="p-3.5 text-left">Version</th>
                <th className="p-3.5 text-left">Status</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? <tr><td colSpan={canDelete ? 9 : 8} className="p-12 text-center text-slate-500">No formulas yet.</td></tr> :
                filtered.map((f) => (
                  <tr key={f.id} className="table-row-hover">
                    {canDelete && <td className="p-3.5"><Checkbox checked={selected.has(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`formula-sel-${f.formula_id}`} /></td>}
                    <td className="p-3.5 mono text-[12px] font-semibold text-slate-900">{f.formula_id}</td>
                    <td className="p-3.5 text-slate-900">{f.name}</td>
                    <td className="p-3.5 mono text-slate-700 text-xs">{f.sku}</td>
                    <td className="p-3.5 text-right font-medium">{Number(f.batch_output_qty || 0).toLocaleString()}</td>
                    <td className="p-3.5 text-slate-600 text-xs">{f.unit}</td>
                    <td className="p-3.5 text-slate-600 text-xs">{f.version}</td>
                    <td className="p-3.5">
                      {f.active
                        ? <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Active</span>
                        : <span className="text-[11px] px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Inactive</span>}
                    </td>
                    <td className="p-3.5 text-right">
                      <div className="flex gap-1 justify-end">
                        {canEdit && <Button variant="ghost" size="icon" onClick={() => startEdit(f)} data-testid={`formula-edit-${f.formula_id}`}><Pencil className="h-4 w-4" /></Button>}
                        {canDelete && <Button variant="ghost" size="icon" onClick={() => remove(f)} data-testid={`formula-del-${f.formula_id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="heading">{editId ? "Edit" : "New"} Formula</DialogTitle><DialogDescription className="text-xs text-slate-500">Only one active formula per SKU will be used in production planning. Saving this as active will deactivate other formulas for the same SKU. SKUs are sourced from SKU Master.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Formula ID</Label><Input value={form.formula_id} onChange={(e) => setForm({ ...form, formula_id: e.target.value })} placeholder="FRM-001" data-testid="formula-id" /></div>
            <div>
              <Label>SKU</Label>
              <SKUMasterCombobox
                value={form.sku}
                onChange={(sku) => setForm({ ...form, sku })}
                placeholder="Pick from SKU Master…"
                testId="formula-sku-combo"
              />
            </div>
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Batch Output Qty</Label><Input type="number" min="0" value={form.batch_output_qty} onChange={(e) => setForm({ ...form, batch_output_qty: e.target.value })} data-testid="formula-batch-qty" /></div>
            <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            <div><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="formula-active" /><Label>Active</Label></div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="formula-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkUploadDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
    </div>
  );
}
